import React, { useMemo, useEffect, useState } from "react";
import { App, Drawer, Input, Space } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { MobileOptionPicker } from "../MobilePicker/MobileOptionPicker";
import { COMMON_DICTS, useMetadataStore, IdType } from "@ifa/utils";
import styles from "./IdDocumentKeyboard.module.css";

export interface IdDocumentValue {
  idType?: IdType;
  idNumber?: string;
}

interface IdDocumentInputProps {
  /**
   * 证件信息（类型 + 号码）
   */
  value?: IdDocumentValue;
  /**
   * 证件信息变化回调
   */
  onChange?: (value: IdDocumentValue) => void;
  /**
   * 是否只读
   */
  readOnly?: boolean;
}

interface ValidationResult {
  valid: boolean;
  message?: string;
}

type KeyboardType = "numeric" | "alphanumeric";

type KeyboardKey =
  | "delete"
  | "clear"
  | "confirm"
  | "switch"
  | string;

interface KeyboardConfig {
  type: KeyboardType;
  numericLeftKey?: string; // 数字键盘左下角
  letterExtraKeys?: string[]; // 字母键盘额外按键
}

interface IdRule {
  minLength: number;
  maxLength: number;
  maxRawLength: number;
  allowedChar: RegExp; // 单字符允许
  normalize: (value: string) => string;
  keyboard: KeyboardConfig;
  validate: (normalized: string) => ValidationResult;
}

const DEFAULT_RULE: IdRule = {
  minLength: 6,
  maxLength: 30,
  maxRawLength: 30,
  allowedChar: /^[A-Z0-9]$/,
  normalize: (value) => value,
  keyboard: { type: "alphanumeric" },
  validate: (normalized) => ({ valid: normalized.length >= 6 }),
};

const ID_RULES: Partial<Record<IdType, IdRule>> = {
  CHINA_ID: {
    minLength: 18,
    maxLength: 18,
    maxRawLength: 18,
    allowedChar: /^[0-9X]$/,
    normalize: (value) => value,
    keyboard: { type: "numeric", numericLeftKey: "X" },
    validate: validateChinaId,
  },
  TAIWAN_ID: {
    minLength: 10,
    maxLength: 10,
    maxRawLength: 10,
    allowedChar: /^[A-Z0-9]$/,
    normalize: (value) => value,
    keyboard: { type: "alphanumeric" },
    validate: validateTaiwanId,
  },
  HONGKONG_ID: {
    minLength: 8,
    maxLength: 9,
    maxRawLength: 11,
    allowedChar: /^[A-Z0-9()]$/,
    normalize: (value) => value.replace(/[()]/g, ""),
    keyboard: { type: "alphanumeric", letterExtraKeys: ["(", ")"] },
    validate: validateHongKongId,
  },
  MACAO_ID: {
    minLength: 7,
    maxLength: 8,
    maxRawLength: 9,
    allowedChar: /^[0-9/]$/,
    normalize: (value) => value.replace(/\//g, ""),
    keyboard: { type: "numeric", numericLeftKey: "/" },
    validate: validateMacaoId,
  },
  PASSPORT: {
    minLength: 6,
    maxLength: 20,
    maxRawLength: 20,
    allowedChar: /^[A-Z0-9]$/,
    normalize: (value) => value,
    keyboard: { type: "alphanumeric" },
    validate: validatePassport,
  },
  HK_MACAU_PASS: {
    minLength: 9,
    maxLength: 9,
    maxRawLength: 9,
    allowedChar: /^[A-Z0-9]$/,
    normalize: (value) => value,
    keyboard: { type: "alphanumeric" },
    validate: validateHkMacauPass,
  },
  BIRTH_CERTIFICATE: {
    minLength: 6,
    maxLength: 30,
    maxRawLength: 30,
    allowedChar: /^[A-Z0-9]$/,
    normalize: (value) => value,
    keyboard: { type: "alphanumeric" },
    validate: validateBirthCertificate,
  },
};

function getRule(idType?: IdType): IdRule {
  if (!idType) return DEFAULT_RULE;
  return ID_RULES[idType] || DEFAULT_RULE;
}

function sanitizeText(text: string) {
  return text.replace(/[\s\u00A0]+/g, "");
}

function normalizeValue(value: string, rule: IdRule) {
  return rule.normalize(value);
}

function toUpperCaseSafe(value: string) {
  return value.toUpperCase();
}

function filterAllowed(text: string, rule: IdRule) {
  let result = "";
  for (const char of text) {
    if (rule.allowedChar.test(char)) {
      result += char;
    }
  }
  return result;
}

function insertTextAt(
  value: string,
  cursor: number,
  text: string,
  rule: IdRule
) {
  const sanitized = sanitizeText(text);
  const upper = toUpperCaseSafe(sanitized);
  const filtered = filterAllowed(upper, rule);

  let newValue = value;
  let newCursor = cursor;

  for (const char of filtered) {
    if (newValue.length >= rule.maxRawLength) {
      break;
    }

    const tentative =
      newValue.slice(0, newCursor) + char + newValue.slice(newCursor);
    const normalized = normalizeValue(tentative, rule);

    if (normalized.length > rule.maxLength) {
      continue;
    }

    newValue = tentative;
    newCursor += 1;
  }

  return { value: newValue, cursor: newCursor };
}

function deleteCharAt(value: string, cursor: number) {
  if (cursor <= 0) return { value, cursor: 0 };
  const newValue = value.slice(0, cursor - 1) + value.slice(cursor);
  return { value: newValue, cursor: cursor - 1 };
}

function shouldValidate(normalized: string, rule: IdRule) {
  if (rule.minLength === rule.maxLength) {
    return normalized.length === rule.maxLength;
  }
  return normalized.length >= rule.minLength;
}

function getValidationResult(value: string, rule: IdRule): ValidationResult {
  const normalized = normalizeValue(value, rule);
  if (!normalized) return { valid: true };
  if (!shouldValidate(normalized, rule)) return { valid: true };
  return rule.validate(normalized);
}

function validateChinaId(value: string): ValidationResult {
  const pattern =
    /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dX]$/;
  if (!pattern.test(value)) {
    return { valid: false, message: "身份證格式錯誤" };
  }

  const weights = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
  const checkCodes = ["1", "0", "X", "9", "8", "7", "6", "5", "4", "3", "2"];

  let sum = 0;
  for (let i = 0; i < 17; i += 1) {
    sum += parseInt(value[i], 10) * weights[i];
  }

  const checkCode = checkCodes[sum % 11];
  if (value[17].toUpperCase() !== checkCode) {
    return { valid: false, message: "身份證校驗碼錯誤" };
  }

  return { valid: true };
}

function validateHongKongId(value: string): ValidationResult {
  const pattern = /^[A-Z]{1,2}\d{6}[0-9A]$/;
  if (!pattern.test(value)) {
    return { valid: false, message: "香港身份證格式錯誤" };
  }
  return { valid: true };
}

function validateTaiwanId(value: string): ValidationResult {
  const pattern = /^[A-Z]\d{9}$/;
  if (!pattern.test(value)) {
    return { valid: false, message: "台灣身份證格式錯誤" };
  }

  const letterValues: Record<string, number> = {
    A: 10,
    B: 11,
    C: 12,
    D: 13,
    E: 14,
    F: 15,
    G: 16,
    H: 17,
    I: 34,
    J: 18,
    K: 19,
    L: 20,
    M: 21,
    N: 22,
    O: 35,
    P: 23,
    Q: 24,
    R: 25,
    S: 26,
    T: 27,
    U: 28,
    V: 29,
    W: 32,
    X: 30,
    Y: 31,
    Z: 33,
  };

  const firstLetter = value[0];
  const letterValue = letterValues[firstLetter];
  if (!letterValue) {
    return { valid: false, message: "台灣身份證首字母錯誤" };
  }

  const weights = [1, 9, 8, 7, 6, 5, 4, 3, 2, 1];
  const digits = [Math.floor(letterValue / 10), letterValue % 10];

  for (let i = 1; i < 10; i += 1) {
    digits.push(parseInt(value[i], 10));
  }

  let sum = 0;
  for (let i = 0; i < 10; i += 1) {
    sum += digits[i] * weights[i];
  }

  if (sum % 10 !== 0) {
    return { valid: false, message: "台灣身份證校驗碼錯誤" };
  }

  return { valid: true };
}

function validateMacaoId(value: string): ValidationResult {
  const pattern = /^\d{7,8}$/;
  if (!pattern.test(value)) {
    return { valid: false, message: "澳門身份證格式錯誤" };
  }
  return { valid: true };
}

function validatePassport(value: string): ValidationResult {
  const pattern = /^[A-Z][A-Z0-9]{5,}$/;
  if (!pattern.test(value)) {
    return { valid: false, message: "護照格式錯誤" };
  }
  return { valid: true };
}

function validateHkMacauPass(value: string): ValidationResult {
  const pattern = /^[A-Z]\d{8}$/;
  if (!pattern.test(value)) {
    return { valid: false, message: "港澳通行證格式錯誤" };
  }
  return { valid: true };
}

function validateBirthCertificate(value: string): ValidationResult {
  const pattern = /^[A-Z0-9]{6,}$/;
  if (!pattern.test(value)) {
    return { valid: false, message: "出生證格式錯誤" };
  }
  return { valid: true };
}

function buildKeyboardLayout(rule: IdRule, letterMode: boolean): KeyboardKey[][] {
  if (rule.keyboard.type === "numeric") {
    return [
      ["1", "2", "3"],
      ["4", "5", "6"],
      ["7", "8", "9"],
      [rule.keyboard.numericLeftKey || "", "0", "delete"],
    ];
  }

  if (!letterMode) {
    return [
      ["1", "2", "3"],
      ["4", "5", "6"],
      ["7", "8", "9"],
      ["switch", "0", "delete"],
    ];
  }

  const extras = rule.keyboard.letterExtraKeys || [];
  const bottomRow: KeyboardKey[] = ["switch", ...extras, "delete"];

  return [
    ["A", "B", "C", "D", "E", "F", "G", "H", "I"],
    ["J", "K", "L", "M", "N", "O", "P", "Q"],
    ["R", "S", "T", "U", "V", "W", "X", "Y", "Z"],
    bottomRow,
  ];
}

const IdDocKeyboardDrawer: React.FC<{
  visible: boolean;
  onClose: () => void;
  onConfirm: (value: string) => void;
  idType?: IdType;
  value: string;
}> = ({ visible, onClose, onConfirm, idType, value }) => {
  const { message } = App.useApp();
  const rule = useMemo(() => getRule(idType), [idType]);
  const [localValue, setLocalValue] = useState(value || "");
  const [letterMode, setLetterMode] = useState(false);

  useEffect(() => {
    if (visible) {
      setLocalValue(value || "");
      setLetterMode(false);
    }
  }, [visible, value]);

  const validation = getValidationResult(localValue, rule);

  const handleKeyPress = (key: KeyboardKey) => {
    if (!key) return;

    if (key === "delete") {
      const next = deleteCharAt(localValue, localValue.length);
      setLocalValue(next.value);
      return;
    }

    if (key === "switch") {
      setLetterMode((prev) => !prev);
      return;
    }

    const next = insertTextAt(localValue, localValue.length, String(key), rule);
    setLocalValue(next.value);
  };

  const handlePasteText = (text: string) => {
    const next = insertTextAt(localValue, localValue.length, text, rule);
    setLocalValue(next.value);
  };

  const handleCopy = async () => {
    if (!localValue) {
      message.info("暫無內容可複製");
      return;
    }

    try {
      await navigator.clipboard.writeText(localValue);
      message.success("已複製");
    } catch (error) {
      message.error("複製失敗");
    }
  };

  const handlePasteFromClipboard = async () => {
    if (!navigator.clipboard || !navigator.clipboard.readText) {
      message.error("目前環境不支援貼上");
      return;
    }

    try {
      const text = await navigator.clipboard.readText();
      if (!text) {
        message.info("剪貼板為空");
        return;
      }
      handlePasteText(text);
      message.success("已貼上");
    } catch (error) {
      message.error("貼上失敗");
    }
  };

  const keys = buildKeyboardLayout(rule, letterMode);

  return (
    <Drawer
      open={visible}
      onClose={() => {
        onConfirm(localValue);
        onClose();
      }}
      placement="bottom"
      height="auto"
      closable={false}
      styles={{ body: { padding: 0 } }}
      zIndex={100000}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <span className={styles.title}>證件號碼輸入</span>
          {validation && !validation.valid && validation.message && (
            <div className={styles.errorTip}>
              <span className={styles.errorText}>{validation.message}</span>
            </div>
          )}
          <div className={styles.headerActions}>
            {localValue && (
              <div className={styles.actionBtn} onClick={handleCopy}>
                複製
              </div>
            )}
            <div className={styles.actionBtn} onClick={handlePasteFromClipboard}>
              貼上
            </div>
            <div
              className={styles.confirmBtn}
              onClick={() => {
                onConfirm(localValue);
                onClose();
              }}
            >
              完成
            </div>
          </div>
        </div>

        <div className={styles.displayArea}>
          <input
            value={localValue}
            readOnly
            className={styles.displayInput}
            placeholder="請輸入證件號"
          />
        </div>

        <div className={styles.keyboardArea}>
          {keys.map((row, rowIndex) => (
            <div key={rowIndex} className={styles.keyRow}>
              {row.map((key, keyIndex) => {
                const isEmpty = key === "";
                const isSpecial =
                  key === "delete" || key === "switch";

                const keyClass = [
                  styles.key,
                  isSpecial ? styles.keySpecial : "",
                  isEmpty ? styles.keyEmpty : "",
                ]
                  .filter(Boolean)
                  .join(" ");

                let label = key;
                if (key === "delete") label = "刪除";
                if (key === "switch") label = letterMode ? "123" : "ABC";

                return (
                  <div
                    key={keyIndex}
                    className={keyClass}
                    onClick={() => handleKeyPress(key)}
                  >
                    {label}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  );
};

export const IdDocumentInput: React.FC<IdDocumentInputProps> = ({
  value = {},
  onChange,
  readOnly = false,
}) => {
  const [pickerVisible, setPickerVisible] = useState(false);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const { selectMetadataList } = useMetadataStore();

  const normalizedValue = useMemo<IdDocumentValue>(() => {
    const result: IdDocumentValue = {};
    if (value?.idType && typeof value.idType === "string") {
      result.idType = value.idType as IdType;
    }
    if (value?.idNumber && typeof value.idNumber === "string") {
      result.idNumber = value.idNumber;
    }
    return result;
  }, [value]);

  const getIdTypeOptions = () => {
    const meta = selectMetadataList.find((m) => m.code === "ID_TYPE");
    if (meta?.items?.length) {
      return meta.items.map((i) => ({ label: i.name || i.key, value: i.key }));
    }
    return COMMON_DICTS.idType;
  };

  const idTypeOptions = getIdTypeOptions();
  const getIdTypeLabel = (val: any) =>
    idTypeOptions.find((o) => o.value === val)?.label;

  const handleIdTypeChange = (idType: string | number) => {
    onChange?.({ ...normalizedValue, idType: idType as IdType });
  };

  const handleDisplayClick = () => {
    if (readOnly) return;

    if (!normalizedValue.idType) {
      setPickerVisible(true);
      return;
    }

    setKeyboardVisible(true);
  };

  const handleKeyboardConfirm = (newValue: string) => {
    onChange?.({ ...normalizedValue, idNumber: newValue });
  };

  return (
    <>
      <Space.Compact style={{ width: "100%" }}>
        <Space.Addon
          onClick={!readOnly ? () => setPickerVisible(true) : undefined}
          style={{
            cursor: readOnly ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            minWidth: 120,
            justifyContent: "space-between",
            gap: 8,
            paddingLeft: 11,
            paddingRight: 11,
          }}
        >
          <span
            style={{
              color: normalizedValue.idType ? "#333" : "var(--placeholder-color)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {getIdTypeLabel(normalizedValue.idType) || "請選擇"}
          </span>
          {!readOnly && (
            <DownOutlined
              style={{
                fontSize: 12,
                color: "var(--placeholder-color)",
              }}
            />
          )}
        </Space.Addon>

        <div style={{ flex: 1, width: 0 }}>
          <Input
            value={normalizedValue.idNumber || ""}
            placeholder="請輸入"
            readOnly
            inputMode="none"
            className={styles.formInput}
            onClick={handleDisplayClick}
            style={{
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              letterSpacing: "0.05em",
              height: "40px",
              fontSize: "16px",
            }}
          />
        </div>
      </Space.Compact>

      <MobileOptionPicker
        visible={pickerVisible}
        onClose={() => setPickerVisible(false)}
        title="證件類型"
        options={idTypeOptions}
        value={normalizedValue.idType}
        onChange={(v) => {
          handleIdTypeChange(v);
          setPickerVisible(false);
        }}
      />

      <IdDocKeyboardDrawer
        visible={keyboardVisible}
        onClose={() => setKeyboardVisible(false)}
        onConfirm={handleKeyboardConfirm}
        idType={normalizedValue.idType}
        value={normalizedValue.idNumber || ""}
      />
    </>
  );
};
